import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/lib/auth-context';
import { jsonFetch } from '@/utils/jsonFetch';

interface Consent {
  status?: string | null;
  method?: string | null;
  notes?: string;
  recorded_at?: string | null;
}

interface Preferences {
  allow_public_shares?: boolean;
  allow_research_opt_in?: boolean;
  retention_policy?: string | null;
  consent?: Consent;
}

interface Shadow {
  last_event?: string | null;
  preferences?: Preferences;
}

interface Student {
  id: string;
  classroom_id: string;
  display_name: string;
  grade?: string | null;
  last_event_at?: string | null;
  shadow?: Shadow;
}

interface StudentState {
  privacyPending?: boolean;
  researchPending?: boolean;
  retentionPending?: string;
  consentStatus?: string;
  consentMethod?: string;
  consentNotes?: string;
  prefSaving?: boolean;
  prefError?: string;
  appendSaving?: boolean;
  appendError?: string;
  exportLoading?: boolean;
  exportError?: string;
  noteDraft?: string;
  descriptorDraft?: string;
}

interface StatesMap {
  [studentId: string]: StudentState;
}

interface ToggleProps {
  label: string;
  description?: string;
  value: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}

interface StudentCardProps {
  student: Student;
  state: StudentState;
  onTogglePrivacy: (value: boolean) => void;
  onToggleResearch: (value: boolean) => void;
  onRetentionChange: (value: string) => void;
  onConsentFieldChange: (field: string, value: string) => void;
  onConsentSubmit: (data: { status: string; method: string; notes: string }) => void;
  onExport: () => void;
  onNoteChange: (value: string) => void;
  onNoteSubmit: () => void;
  onDescriptorChange: (value: string) => void;
  onDescriptorSubmit: () => void;
}

interface RosterStudent {
  id: string;
  classroom_id: string;
  display_name?: string;
  full_name?: string;
  grade?: string | null;
}

interface LedgerRow {
  student_id: string;
  last_event_at?: string | null;
  shadow?: Shadow;
}

interface PrefsUpdate {
  allow_public_shares?: boolean;
  allow_research_opt_in?: boolean;
  retention_policy?: string | null;
  consent?: Consent;
}

const RETENTION_OPTIONS = [
  { value: '', label: 'Keep until I decide' },
  { value: 'archive_6_months', label: 'Archive after 6 months' },
  { value: 'archive_12_months', label: 'Archive after 12 months' },
  { value: 'archive_24_months', label: 'Archive after 24 months' },
  { value: 'archive_36_months', label: 'Archive after 36 months' },
];

const CONSENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received' },
  { value: 'revoked', label: 'Revoked' },
];

const CONSENT_METHOD_OPTIONS = [
  { value: 'upload', label: 'Uploaded Document' },
  { value: 'paper', label: 'Paper form on file' },
  { value: 'verbal', label: 'Documented verbal consent' },
];

function Toggle({ label, description, value, onChange, disabled }: ToggleProps) {
  return (
    <div className="flex items-start justify-between gap-4 rounded-xl border border-sand/70 bg-white/80 p-4">
      <div>
        <p className="font-medium text-cocoa">{label}</p>
        {description ? <p className="mt-1 text-sm text-cocoa/70">{description}</p> : null}
      </div>
      <button
        type="button"
        className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition ${
          value ? 'bg-emerald-500' : 'bg-sand'
        } ${disabled ? 'opacity-60' : ''}`}
        role="switch"
        aria-checked={value}
        onClick={() => onChange(!value)}
        disabled={disabled}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition ${
            value ? 'translate-x-5' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );
}

function StudentCard({
  student,
  state,
  onTogglePrivacy,
  onToggleResearch,
  onRetentionChange,
  onConsentFieldChange,
  onConsentSubmit,
  onExport,
  onNoteChange,
  onNoteSubmit,
  onDescriptorChange,
  onDescriptorSubmit,
}: StudentCardProps) {
  const preferences = student.shadow?.preferences ?? {};
  const consent = preferences.consent ?? {};
  const pendingPrivacy = state.privacyPending;
  const pendingResearch = state.researchPending;
  const pendingRetention = state.retentionPending;

  const allowPublic = pendingPrivacy ?? !!preferences.allow_public_shares;
  const allowResearch = pendingResearch ?? !!preferences.allow_research_opt_in;
  const retentionValue = pendingRetention ?? preferences.retention_policy ?? '';

  const consentStatus =
    state.consentStatus ?? consent.status ?? CONSENT_STATUS_OPTIONS[0].value;
  const consentMethod = state.consentMethod ?? consent.method ?? CONSENT_METHOD_OPTIONS[0].value;
  const consentNotes = state.consentNotes ?? '';

  const lastEvent = student.shadow?.last_event || student.last_event_at;

  const noteDraft = state.noteDraft ?? '';
  const descriptorDraft = state.descriptorDraft ?? '';

  return (
    <article className="space-y-5 rounded-3xl border border-sand bg-white/80 p-6 shadow-soft">
      <header className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-cocoa">{student.display_name}</h3>
          <p className="text-sm text-cocoa/70">
            {student.grade ? `Grade ${student.grade}` : 'Grade level pending'} ·{' '}
            {lastEvent ? `Last event ${new Date(lastEvent).toLocaleString()}` : 'No ledger activity yet'}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-secondary whitespace-nowrap"
          onClick={onExport}
          disabled={state.exportLoading}
        >
          {state.exportLoading ? 'Preparing…' : 'Export JSON'}
        </button>
      </header>

      {state.prefError ? (
        <div className="rounded-xl border border-brick/40 bg-brick/10 px-3 py-2 text-sm text-brick">
          {state.prefError}
        </div>
      ) : null}
      {state.appendError ? (
        <div className="rounded-xl border border-brick/40 bg-brick/10 px-3 py-2 text-sm text-brick">
          {state.appendError}
        </div>
      ) : null}

      <section className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Privacy</h4>
        <Toggle
          label="Allow public share links"
          description="Controls whether you can generate limited student share links. Default is off."
          value={allowPublic}
          onChange={onTogglePrivacy}
          disabled={state.prefSaving}
        />
        <Toggle
          label="Permit anonymized research"
          description="Opt in to sharing aggregated, anonymized learning signals."
          value={allowResearch}
          onChange={onToggleResearch}
          disabled={state.prefSaving}
        />
      </section>

      <section className="space-y-2">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Retention</h4>
        <label className="block text-sm text-cocoa/70">
          <span className="sr-only">Retention policy</span>
          <select
            className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60"
            value={retentionValue}
            onChange={(event) => onRetentionChange(event.target.value)}
            disabled={state.prefSaving}
          >
            {RETENTION_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Consent</h4>
        <div className="grid gap-3 sm:grid-cols-2">
          <label className="text-sm text-cocoa/70">
            Status
            <select
              className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60"
              value={consentStatus}
              onChange={(event) => onConsentFieldChange('consentStatus', event.target.value)}
              disabled={state.prefSaving}
            >
              {CONSENT_STATUS_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm text-cocoa/70">
            Method
            <select
              className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60"
              value={consentMethod}
              onChange={(event) => onConsentFieldChange('consentMethod', event.target.value)}
              disabled={state.prefSaving}
            >
              {CONSENT_METHOD_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="block text-sm text-cocoa/70">
          Notes (optional)
          <textarea
            className="mt-1 w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60"
            rows={2}
            value={consentNotes}
            onChange={(event) => onConsentFieldChange('consentNotes', event.target.value)}
            placeholder="Upload link, guardian contact, or follow-up reminder"
          />
        </label>
        <div className="flex justify-end">
          <button
            type="button"
            className="btn"
            onClick={() => onConsentSubmit({ status: consentStatus, method: consentMethod, notes: consentNotes })}
            disabled={state.prefSaving}
          >
            {state.prefSaving ? 'Saving…' : 'Save consent update'}
          </button>
        </div>
        {consent.recorded_at ? (
          <p className="text-xs text-cocoa/60">
            Last recorded {new Date(consent.recorded_at).toLocaleString()} ({consent.status || 'pending'})
          </p>
        ) : null}
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Teacher Notes</h4>
        <textarea
          className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60"
          rows={3}
          value={noteDraft}
          onChange={(event) => onNoteChange(event.target.value)}
          placeholder="Add a quick note for this student"
        />
        <div className="flex justify-end">
          <button
            type="button"
            className="btn"
            onClick={onNoteSubmit}
            disabled={!noteDraft.trim() || state.appendSaving}
          >
            {state.appendSaving ? 'Saving…' : 'Append note'}
          </button>
        </div>
      </section>

      <section className="space-y-3">
        <h4 className="text-sm font-semibold uppercase tracking-wide text-cocoa/60">Descriptors</h4>
        <textarea
          className="w-full rounded-xl border border-sand bg-white px-3 py-2 text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-mint/60"
          rows={2}
          value={descriptorDraft}
          onChange={(event) => onDescriptorChange(event.target.value)}
          placeholder="Update focus areas, strengths, or supports"
        />
        <div className="flex justify-end">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onDescriptorSubmit}
            disabled={!descriptorDraft.trim() || state.appendSaving}
          >
            {state.appendSaving ? 'Saving…' : 'Append descriptor update'}
          </button>
        </div>
      </section>
    </article>
  );
}

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

export default function TeacherSettings() {
  const { session } = useAuth();
  const teacherId = session?.user?.id ?? null;

  const [students, setStudents] = useState<Student[]>([]);
  const [states, setStates] = useState<StatesMap>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const setStudentState = useCallback((id: string, patch: Partial<StudentState> | ((current: StudentState) => Partial<StudentState>)) => {
    setStates((prev: StatesMap) => {
      const current = prev[id] || {};
      const nextPatch = typeof patch === 'function' ? patch(current) : patch;
      return { ...prev, [id]: { ...current, ...nextPatch } };
    });
  }, []);

  const updateStudent = useCallback((id: string, updater: Partial<Student> | ((student: Student) => Student)) => {
    setStudents((prev: Student[]) =>
      prev.map((student: Student) => {
        if (student.id !== id) return student;
        const next = typeof updater === 'function' ? updater(student) : { ...student, ...updater };
        return next;
      }),
    );
  }, []);

  const loadStudents = useCallback(async () => {
    if (!teacherId) {
      setStudents([]);
      return;
    }
    setLoading(true);
    setError('');
    try {
      const rawRoster = await jsonFetch<{ data?: { students?: RosterStudent[] }; students?: RosterStudent[] }>(`${CORE_API}/api/v1/students`);
      const rosterPayload = (rawRoster?.data ?? rawRoster) as { students?: RosterStudent[] };
      const roster: RosterStudent[] = Array.isArray(rosterPayload?.students) ? rosterPayload.students : [];
      if (!roster.length) {
        setStudents([]);
        return;
      }
      const classroomIds: string[] = Array.from(
        new Set(roster.map((item: RosterStudent) => item.classroom_id).filter(Boolean)),
      );
      const ledgerMap = new Map<string, LedgerRow>();
      await Promise.all(
        classroomIds.map(async (classroomId: string) => {
          try {
            const data = await jsonFetch<{ students?: LedgerRow[] }>(`${CORE_API}/api/v1/students/ledger?classroom_id=${encodeURIComponent(classroomId)}`);
            (data?.students || []).forEach((row: LedgerRow) => {
              ledgerMap.set(row.student_id, row);
            });
          } catch (ledgerError) {
            console.warn('[TeacherSettings] ledger fetch failed', ledgerError);
          }
        }),
      );

      const combined: Student[] = roster.map((student: RosterStudent) => {
        const ledger = ledgerMap.get(student.id);
        return {
          id: student.id,
          classroom_id: student.classroom_id,
          display_name: student.display_name ?? student.full_name ?? 'Student',
          grade: student.grade ?? null,
          last_event_at: ledger?.last_event_at ?? null,
          shadow: ledger?.shadow ?? { preferences: {} },
        };
      });
      setStudents(combined);
    } catch (err) {
      console.error('[TeacherSettings] roster load failed', err);
      setError('We could not load your student data. Try again in a moment.');
      setStudents([]);
    } finally {
      setLoading(false);
    }
  }, [teacherId]);

  useEffect(() => {
    loadStudents();
  }, [loadStudents]);

  useEffect(() => {
    setStates((prev: StatesMap) => {
      const next: StatesMap = {};
      students.forEach((student: Student) => {
        next[student.id] = prev[student.id] || {};
      });
      return next;
    });
  }, [students]);

  const savePreferences = useCallback(
    async (student: Student, prefsUpdate: PrefsUpdate) => {
      setStudentState(student.id, { prefSaving: true, prefError: '' });
      try {
        await jsonFetch(`${CORE_API}/api/v1/students/ledger/preferences`, {
          method: 'POST',
          body: JSON.stringify({
            student_id: student.id,
            classroom_id: student.classroom_id,
            preferences: prefsUpdate,
          }),
        });
        const nowIso = new Date().toISOString();
        updateStudent(student.id, (current: Student) => {
          const nextPreferences: Preferences = { ...(current.shadow?.preferences ?? {}) };
          if ('allow_public_shares' in prefsUpdate) {
            nextPreferences.allow_public_shares = !!prefsUpdate.allow_public_shares;
          }
          if ('allow_research_opt_in' in prefsUpdate) {
            nextPreferences.allow_research_opt_in = !!prefsUpdate.allow_research_opt_in;
          }
          if ('retention_policy' in prefsUpdate) {
            nextPreferences.retention_policy = prefsUpdate.retention_policy ?? '';
          }
          if (prefsUpdate.consent) {
            nextPreferences.consent = {
              status: prefsUpdate.consent.status ?? null,
              method: prefsUpdate.consent.method ?? null,
              recorded_at: nowIso,
            };
          }
          return {
            ...current,
            last_event_at: nowIso,
            shadow: {
              ...(current.shadow ?? {}),
              last_event: nowIso,
              preferences: nextPreferences,
            },
          };
        });
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to save preference';
        setStudentState(student.id, { prefError: message });
        throw err;
      } finally {
        setStudentState(student.id, { prefSaving: false, privacyPending: undefined, researchPending: undefined, retentionPending: undefined });
      }
    },
    [setStudentState, updateStudent],
  );

  const appendEvent = useCallback(
    async (student: Student, kind: string, payload: Record<string, unknown>) => {
      setStudentState(student.id, { appendSaving: true, appendError: '' });
      try {
        await jsonFetch(`${CORE_API}/api/v1/students/ledger/append`, {
          method: 'POST',
          body: JSON.stringify({
            student_id: student.id,
            classroom_id: student.classroom_id,
            kind,
            payload,
          }),
        });
        const nowIso = new Date().toISOString();
        updateStudent(student.id, (current: Student) => ({
          ...current,
          last_event_at: nowIso,
          shadow: {
            ...(current.shadow ?? {}),
            last_event: nowIso,
            preferences: current.shadow?.preferences ?? {},
          },
        }));
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to save entry';
        setStudentState(student.id, { appendError: message });
        throw err;
      } finally {
        setStudentState(student.id, { appendSaving: false });
      }
    },
    [setStudentState, updateStudent],
  );

  const exportStudent = useCallback(
    async (student: Student) => {
      setStudentState(student.id, { exportLoading: true, exportError: '' });
      try {
        const data = await jsonFetch(`${CORE_API}/api/v1/students/ledger/export?student_id=${student.id}`);
        const jsonString = JSON.stringify(data, null, 2);
        const blob = new Blob([jsonString], { type: 'application/json' });
        const safeName = (student.display_name || 'student')
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-+|-+$/g, '') || 'student';
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `student-${safeName || student.id}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Unable to export student data';
        setStudentState(student.id, { prefError: message });
      } finally {
        setStudentState(student.id, { exportLoading: false });
      }
    },
    [setStudentState],
  );

  const content = useMemo(() => {
    if (loading) {
      return (
        <div className="rounded-3xl border border-sand bg-white/70 p-6 text-center text-sm text-cocoa/70">
          Loading student data…
        </div>
      );
    }

    if (error) {
      return (
        <div className="rounded-3xl border border-brick/30 bg-brick/10 p-6 text-sm text-brick">{error}</div>
      );
    }

    if (!students.length) {
      return (
        <div className="rounded-3xl border border-sand bg-white/70 p-6 text-center text-sm text-cocoa/70">
          Add students to your roster to manage privacy, consent, and exports.
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {students.map((student) => (
          <StudentCard
            key={student.id}
            student={student}
            state={states[student.id] || {}}
            onTogglePrivacy={(value) => {
              setStudentState(student.id, { privacyPending: value, prefError: '' });
              savePreferences(student, { allow_public_shares: value }).catch(() => {});
            }}
            onToggleResearch={(value) => {
              setStudentState(student.id, { researchPending: value, prefError: '' });
              savePreferences(student, { allow_research_opt_in: value }).catch(() => {});
            }}
            onRetentionChange={(value) => {
              setStudentState(student.id, { retentionPending: value, prefError: '' });
              savePreferences(student, { retention_policy: value || null }).catch(() => {});
            }}
            onConsentFieldChange={(field, value) => {
              setStudentState(student.id, { [field]: value });
            }}
            onConsentSubmit={({ status, method, notes }) => {
              savePreferences(student, {
                consent: {
                  status,
                  method,
                  notes: notes?.trim() ? notes.trim() : undefined,
                  recorded_at: new Date().toISOString(),
                },
              })
                .then(() => {
                  setStudentState(student.id, { consentNotes: '', consentStatus: status, consentMethod: method });
                })
                .catch(() => {});
            }}
            onExport={() => exportStudent(student)}
            onNoteChange={(value) => setStudentState(student.id, { noteDraft: value })}
            onNoteSubmit={() => {
              const note = (states[student.id]?.noteDraft || '').trim();
              if (!note) return;
              appendEvent(student, 'teacher_note', { text: note })
                .then(() => setStudentState(student.id, { noteDraft: '' }))
                .catch(() => {});
            }}
            onDescriptorChange={(value) => setStudentState(student.id, { descriptorDraft: value })}
            onDescriptorSubmit={() => {
              const descriptor = (states[student.id]?.descriptorDraft || '').trim();
              if (!descriptor) return;
              appendEvent(student, 'descriptor_update', { descriptor })
                .then(() => setStudentState(student.id, { descriptorDraft: '' }))
                .catch(() => {});
            }}
          />
        ))}
      </div>
    );
  }, [appendEvent, error, exportStudent, loading, savePreferences, states, students]);

  return (
    <div className="space-y-6">
      <header className="space-y-2">
        <p className="text-sm uppercase tracking-wide text-cocoa/60">Settings</p>
        <h1 className="text-3xl font-semibold text-cocoa">Student Data</h1>
        <p className="text-cocoa/70">
          Manage privacy defaults, capture guardian consent, export encrypted ledgers, and append quick updates to each student’s SmartBlock history.
        </p>
      </header>
      {content}
    </div>
  );
}
